import { useEffect, useRef, useState } from 'react'
import posthog from 'posthog-js'

import { ResultCode, StepNames } from '@src/enums'
import { createContextLogger } from '@src/utils'
import { isPotentiallyCCTP } from '@src/utils/exchangeUtils'
import { useApplicationStore, useHistoryStore } from '@src/zustand_stores'

// Default timeout limit for regular transactions (10 minutes)
const DEFAULT_TIMEOUT_LIMIT = 60 * 10
// Extended timeout limit for CCTP transactions (20 minutes)
const CCTP_TIMEOUT_LIMIT = 60 * 20

const logger = createContextLogger('useFailOnTimeout')

export function useFailOnTimeout() {
    const { exchangeId } = useApplicationStore()
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const getOrder = useHistoryStore(state => state.getOrder)
    const transactionData = getOrder(exchangeId)

    // Ref to hold the timeout identifier.
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const clearTimeoutTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Ref to track if the pending state timer has been started.
    const timerStartedRef = useRef(false)

    const timeoutOccurredRef = useRef(false)
    const [timeoutEventFired, setTimeoutEventFired] = useState(false)
    
    // Function to track the timeout event that works with tab visibility
    const trackTimeoutEvent = (exchangeId: string, timeoutLimit: number, isCCTP: boolean, currentStep: string) => {
        // Only track once
        if (timeoutEventFired) return;
        
        // Track the timeout event in PostHog
        posthog.capture('trade_timeout_fe', {
            exchange_id: exchangeId,
            timeout_limit_seconds: timeoutLimit,
            is_cctp: isCCTP,
            step: currentStep,
            timestamp: new Date().toISOString(),
            was_tabbed_in: document.visibilityState === 'visible'
        });
        
        setTimeoutEventFired(true);
    }

    useEffect(() => {
        // Log the transaction data for debugging.
        if (transactionData) {
            logger.info(
                `transactionData.exchangeId = ${transactionData.exchangeId}\n` +
                    `transactionData.currentStep = ${transactionData.currentStep}\n` +
                    `transactionData.result = ${JSON.stringify(transactionData.result)}`
            )
        }
    }, [transactionData])

    useEffect(() => {
        // Only act if transactionData exists.
        if (!transactionData) return

        // Check if we are in a pending state.
        const isPending =
            transactionData.currentStep === StepNames.MATCHING ||
            transactionData.currentStep === StepNames.MATCH_CREATED

        // Check if transaction has succeeded.
        const isSuccess =
            transactionData.currentStep === StepNames.MATCH_EXECUTED &&
            transactionData.result?.status === ResultCode.success

        // Determine if this is a CCTP transaction based on tokens and chain IDs
        const isCCTP =
            transactionData.sellData && transactionData.buyData
                ? isPotentiallyCCTP(
                      transactionData.sellData.token?.symbol,
                      transactionData.buyData.token?.symbol,
                      transactionData.sellData.chainId,
                      transactionData.buyData.chainId
                  )
                : false

        // Use appropriate timeout based on transaction type
        const TIMEOUT_LIMIT = isCCTP ? CCTP_TIMEOUT_LIMIT : DEFAULT_TIMEOUT_LIMIT

        if (isPending && !timerStartedRef.current) {
            // Start the timer only once when first entering a pending state.
            logger.info(`Starting timeout watcher for exchangeId ${exchangeId} at step ${transactionData.currentStep}`)
            logger.info(`Using ${isCCTP ? 'CCTP' : 'default'} timeout limit: ${TIMEOUT_LIMIT} seconds`)
            timerStartedRef.current = true
            timeoutRef.current = setTimeout(() => {
                logger.error(`Transaction timed out for exchangeId ${exchangeId}`)
                timeoutOccurredRef.current = true;
                
                // Track the timeout event and update state only if the document is visible
                trackTimeoutEvent(exchangeId, TIMEOUT_LIMIT, isCCTP, transactionData.currentStep);
                
                updateHistory(exchangeId, {
                    currentStep: StepNames.MATCH_TIMED_OUT,
                    result: {
                        status: ResultCode.failure,
                        message: 'Your swap has timed out. Please try again.',
                    },
                })
            }, TIMEOUT_LIMIT * 1000)

            clearTimeoutTimeoutRef.current = setTimeout(
                () => {
                    logger.error(`Clearing timeout to avoid memory leak`)
                    timeoutRef.current = null
                    clearTimeoutTimeoutRef.current = null
                },
                2 * TIMEOUT_LIMIT * 1000
            )
            logger.info(`Timeout started for ${exchangeId}`)
        } else if (isSuccess) {
            // If the transaction succeeded, clear any pending timer and reset our flag.
            logger.info(`Transaction succeeded for exchangeId ${exchangeId} - clearing timeout.`)
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
                clearTimeoutTimeoutRef.current = null
                logger.info('Timeout cleared.')
            }
            timerStartedRef.current = false
        }
    }, [exchangeId, transactionData, updateHistory])

    useEffect(() => {
        // Function to check if timeout occurred but event wasn't fired
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && timeoutOccurredRef.current) {
                // If the timeout occurred while tabbed out, fire the event when tabbed back in
                if (transactionData && transactionData.currentStep === StepNames.MATCH_TIMED_OUT) {
                    // Check if it's a CCTP transaction based on sell and buy tokens
                    const sellToken = transactionData.sellData?.token?.symbol
                    const buyToken = transactionData.buyData?.token?.symbol
                    const sellChainId = transactionData.sellData?.chainId
                    const buyChainId = transactionData.buyData?.chainId
                    
                    // Use the same isPotentiallyCCTP function that's used elsewhere
                    const isCCTPTransaction = sellToken && buyToken && sellChainId && buyChainId
                        ? isPotentiallyCCTP(sellToken, buyToken, sellChainId, buyChainId)
                        : false
                    // Use appropriate timeout based on transaction type
                    const timeoutLimit = isCCTPTransaction ? CCTP_TIMEOUT_LIMIT : DEFAULT_TIMEOUT_LIMIT
                    trackTimeoutEvent(exchangeId, timeoutLimit, isCCTPTransaction, transactionData.currentStep)
                }
            }
        }
        
        // Add visibility change listener
        document.addEventListener('visibilitychange', handleVisibilityChange)
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [exchangeId, transactionData])

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
            if (clearTimeoutTimeoutRef.current) {
                clearTimeout(clearTimeoutTimeoutRef.current)
            }
        }
    }, [])

    return null // This hook doesn't render anything.
}
